"use client";

import Link from "next/link";
import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CostDocLogo } from "@/components/site/CostDocLogo";
import { CONTACT_EMAIL, CONTACT_EMAIL_LINK } from "@/lib/constants";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);
  return (
    <div className="container flex items-start justify-center py-8 md:min-h-[60vh] md:items-center md:py-16">
      <div className="w-full max-w-2xl space-y-8">
        {/* Brand header */}
        <div className="flex items-center gap-3">
          <Link href="/" aria-label="CostDoc home">
            <CostDocLogo />
          </Link>
        </div>

        <div className="rounded-2xl border border-[var(--color-border)] bg-white p-8 md:p-10 shadow-sm">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <AlertTriangle size={28} aria-hidden="true" />
          </div>
          <p className="mt-5 section-label">Something went wrong</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-[var(--color-primary)]">
            We couldn&apos;t load this page right now
          </h1>
          <p className="mt-4 text-lg text-[var(--color-muted)]">
            This is usually a temporary glitch. Your cost data is safe, so try reloading the page or heading back to the homepage.
          </p>
          {error.digest && (
            <p className="mt-3 font-mono text-xs text-[var(--color-muted)]">
              Ref: {error.digest}
            </p>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={reset} className="min-h-11 gap-2 px-4">
              <RefreshCw size={16} aria-hidden="true" />
              Reload page
            </Button>
            <Button asChild variant="tertiary" className="min-h-11 px-4">
              <Link href="/">Back to homepage</Link>
            </Button>
          </div>

          <div className="mt-6 section-rule" />

          <p className="mt-6 text-sm text-[var(--color-muted)]">
            Still stuck?{" "}
            <a
              href={CONTACT_EMAIL_LINK}
              className="font-medium text-[var(--color-accent)] hover:underline"
            >
              Email {CONTACT_EMAIL}
            </a>{" "}
            and we&apos;ll get you sorted.
          </p>
        </div>

        {/* Footer strip */}
        <p className="text-center text-sm text-[var(--color-muted)]">
          <Link href="/" className="font-medium text-[var(--color-accent)] hover:underline">
            CostDoc
          </Link>
          {" "}: Australia&apos;s independent medical cost transparency platform.
        </p>
      </div>
    </div>
  );
}
